import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { usePermission } from '@/lib/permissions'
import { FEATURES } from '@/lib/featureFlags'
import { useFeatureFlag } from '@/providers/FeatureFlagsProvider'
import { isOccupiable } from '@/lib/storageTree'
import { crumbsFor, gridCellsFromTree, hasChildren } from '@/lib/storageBrowse'
import { fillFromCapacity } from '@/lib/slotMetrics'
import { resolveKeptAt } from '@/lib/keptAt'
import { unitStatus } from '@/lib/storageList'
import {
  equipmentSlotHint,
  isInUnit,
  mapNodeFor,
  NO_INVENTORY_ACCESS,
  ownerUnitOf,
  panelTabs,
  resolvePanelTab,
  settingsEntryPoints,
  storageListHref,
  unitPageHref,
  unitSummaryParts,
  type PanelTab,
  type UnitSurface,
} from '@/lib/storageUnitView'
import {
  useCapacitySummary,
  useGridCells,
  useLazyStorageTree,
  useNodeContents,
  useStorageCapacity,
  useStorageNode,
  useStorageRoots,
} from '@/hooks/useStorage'
import { useStorageZones } from '@/hooks/usePickLists'
import type { ApiStorageNode, LocateResult, StorageTreeNode } from '@/types/storage'
import { DeclareGridForm } from '@/components/inventory/storage/LayoutControls'
import { ReceiveStockDialogs } from '@/components/inventory/storage/ReceiveStockButton'
import { SlotMap, SlotMapLegend } from '@/components/inventory/storage/SlotMap'
import { StorageBrowser } from '@/components/inventory/storage/StorageBrowser'
import { StorageWizardDialog } from '@/components/inventory/storage/StorageWizardDialog'
import { UnitBreadcrumb } from '@/components/inventory/storage/unit/UnitBreadcrumb'
import { UnitHeader } from '@/components/inventory/storage/unit/UnitHeader'
import { UnitWhereabouts } from '@/components/inventory/storage/unit/UnitWhereabouts'
import { UnitSidePanel } from '@/components/inventory/storage/unit/UnitSidePanel'
import { NodeSettingsForm } from '@/components/inventory/storage/unit/NodeSettingsForm'
import { UnitAvailabilityBanner } from '@/components/equipment/storage/UnitAvailabilityBanner'
import { UnitMaintenancePanel } from '@/components/equipment/storage/UnitMaintenancePanel'
import { ReplaceUnitDialog } from '@/components/equipment/storage/replace/ReplaceUnitDialog'
import { DeleteStorageUnitDialog } from '@/components/equipment/storage/DeleteStorageUnitDialog'

const KBD = 'rounded border bg-muted px-1 font-mono text-[10px] text-foreground'

/** Cells may grow to this so a small grid fills the map card rather than
 *  leaving a wide empty strip beside it. Still square, so still a shelf. */
const MAX_CELL_PX = 160

/**
 * One storage unit: the map first, what is selected in a side panel, and its
 * settings in a modal.
 *
 * Both `/inventory/storage/:unitId` and `/equipment/storage/:unitId` render
 * this, each under its own route guard — there is no redirect between them,
 * because the equipment route deliberately asks only for the equipment
 * feature. What differs is what is shown: the parts that read inventory routes
 * (contents, put-away, find, history) need the inventory feature and
 * `inventory.view`, and are left out without them rather than rendered to 403.
 *
 * The selection and the panel tab are in the URL (`?node=`, `?panel=`) so a
 * link or a reload lands in the same place. `?panel=settings` opens Settings
 * (and leaves the URL when it closes); `?setup=1` opens the wizard.
 */
export function StorageUnitView({ surface }: { surface: UnitSurface }) {
  const { unitId } = useParams<{ unitId: string }>()
  // Keyed on the unit: a dialog, a put-away or a clicked slot from the last
  // unit means nothing on the next one (Find can jump between them).
  return <UnitView key={unitId} surface={surface} unitId={unitId} />
}

function UnitView({ surface, unitId }: { surface: UnitSurface; unitId: string | undefined }) {
  const [params, setParams] = useSearchParams()
  const navigate = useNavigate()
  const location = useLocation()
  const { can } = usePermission()

  // Equipment is the configuration view: the layout, never what is stored.
  const layoutOnly = surface === 'equipment'
  const inventoryParts = useFeatureFlag(FEATURES.inventory) && can('inventory.view')
  const canConfigure = can('inventory.configure')
  const canManage = inventoryParts && can('inventory.manage')

  // Router state, so a reload loses it and falls back to the surface's list.
  const listHref = storageListHref(surface, (location.state as { from?: string } | null)?.from)

  const roots = useStorageRoots()
  // Containers only, a level at a time, each carrying its own capacity: a
  // freezer's ten thousand boxes are fetched a rack at a time, as opened.
  const tree = useLazyStorageTree(unitId ?? null)
  const capacity = useStorageCapacity(unitId ?? null)
  const summary = useCapacitySummary(inventoryParts)
  const zones = useStorageZones()
  const nodes = tree.nodes
  const { ensureLoaded } = tree

  const unit = nodes.find((n) => n.id === unitId) ?? null
  const isEmpty = unit != null && !unit.gridRows && (tree.containerCount ?? nodes.length - 1) === 0

  const [wizardOpen, setWizardOpen] = useState(false)
  const [replacing, setReplacing] = useState(false)
  const [deleting, setDeleting] = useState(false)
  // "Put stock here" for the selected slot, or receiving into the unit.
  const [placingFor, setPlacingFor] = useState<string | null>(null)
  const [receiving, setReceiving] = useState(false)
  // The node Settings is open for: the unit from the header, the selection
  // from Edit settings.
  const [settingsFor, setSettingsFor] = useState<string | null>(null)

  /** Write `?node=` / `?panel=` without stacking a history entry per click. */
  const patchParams = (patch: Record<string, string | null>) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        for (const [key, value] of Object.entries(patch)) {
          if (value == null) next.delete(key)
          else next.set(key, value)
        }
        return next
      },
      { replace: true },
    )

  // Consume the flag rather than leave it in the URL: a reload should not
  // reopen the wizard over a unit that has since been described. Without
  // `inventory.configure` the wizard could only fail, so the flag is dropped.
  useEffect(() => {
    if (params.get('setup') !== '1') return
    if (canConfigure) setWizardOpen(true)
    const next = new URLSearchParams(params)
    next.delete('setup')
    setParams(next, { replace: true })
  }, [params, setParams, canConfigure])

  /**
   * The selection, and its address.
   *
   * A slot is not in the tree, so its address is what finds the box to draw:
   * it comes with the click, or — for a link or reload, which carry only the
   * id — from the node itself.
   */
  const selectedId = params.get('node')
  const [clicked, setClicked] = useState<{ id: string; path: string } | null>(null)
  const inTree = selectedId ? nodes.find((n) => n.id === selectedId) : undefined
  const knownPath = inTree?.path ?? (clicked && clicked.id === selectedId ? clicked.path : null)
  const fetched = useStorageNode(selectedId && !knownPath ? selectedId : null)
  const selectedPath = knownPath ?? fetched.data?.path ?? null

  // A `?node=` that names nothing, or a node in another unit, is dropped so
  // the page shows the unit itself rather than a selection it cannot draw.
  const foreignNode =
    !!selectedId &&
    !knownPath &&
    unit != null &&
    (fetched.isError || (fetched.data != null && !isInUnit(fetched.data.path, unit.path)))
  useEffect(() => {
    if (foreignNode) patchParams({ node: null })
  }, [foreignNode]) // patchParams is rebuilt every render; the flag is the trigger

  // Open every level down to the selection, so a link deep into the unit
  // lands with its box drawn and its breadcrumb whole. Each step is a lookup
  // in what is loaded, and `ensureLoaded` ignores a level already asked for.
  useEffect(() => {
    if (!selectedPath) return
    const segments = selectedPath.split('/')
    for (let i = 1; i <= segments.length; i++) {
      const level = nodes.find((n) => n.path === segments.slice(0, i).join('/'))
      if (level && hasChildren(nodes, level.id)) ensureLoaded(level.id)
    }
  }, [selectedPath, nodes, ensureLoaded])

  const { holder, gridNode } = mapNodeFor(nodes, unit, foreignNode ? null : selectedId, foreignNode ? null : selectedPath)

  /**
   * A grid of CONTAINERS is drawn from the tree; only a box asks `/cells`,
   * because its children are slots and a slot has no row until something
   * lands in it.
   */
  const fromTree = gridNode ? gridCellsFromTree(gridNode, nodes) : null
  const cells = useGridCells(fromTree === null ? (gridNode?.id ?? null) : null)
  // A just-opened rack whose level is still in flight would draw every box
  // blank, which reads as "nothing here" — the one wrong answer.
  const gridPending =
    gridNode != null && fromTree != null && fromTree.length === 0 && hasChildren(nodes, gridNode.id)
  const gridCells = useMemo(() => fromTree ?? cells.data?.cells ?? [], [fromTree, cells.data])
  // Layout-only maps leave occupancy out, so every slot reads as in service
  // or blocked.
  const occupiedIds = useMemo(
    () => new Set(layoutOnly ? [] : gridCells.filter((c) => c.occupied).map((c) => c.id)),
    [gridCells, layoutOnly],
  )
  const fills = useMemo(() => new Map(nodes.map((n) => [n.id, fillFromCapacity(n.capacity)])), [nodes])

  // What is in each slot on screen, for the lot under each code. Only for a
  // grid of slots — a container cell shows a fill bar, never a lot.
  const showContents = inventoryParts && !layoutOnly
  const gridContents = useNodeContents(showContents && fromTree === null ? (gridNode?.id ?? null) : null)
  const lotsByNodeId = useMemo(() => {
    const map = new Map<string, LocateResult[]>()
    for (const lot of gridContents.data?.lots ?? []) {
      const bucket = map.get(lot.nodeId)
      if (bucket) bucket.push(lot)
      else map.set(lot.nodeId, [lot])
    }
    return map
  }, [gridContents.data])

  // The panel describes the selection — found in the tree, or among the cells
  // drawn — and falls back to the unit, so arriving describes the freezer.
  const selected: ApiStorageNode | null = inTree ?? gridCells.find((c) => c.id === selectedId) ?? holder
  const selectedIsSlot = selected != null && isOccupiable(selected.kind)

  const tabs = panelTabs({ surface, inventoryParts })
  const tab = resolvePanelTab(params.get('panel'), tabs)

  // The header's ⚙ Settings is always the unit's; Edit settings on the
  // Selected card is the selection's. Each shows only when the modal would
  // hold something this user may do for that node.
  const canReplace = can('storage.replace') && !!unit?.equipmentId
  const entry = settingsEntryPoints({
    canConfigure,
    canReplaceUnit: canReplace,
    selectedIsUnit: selected != null && selected.id === unit?.id,
  })
  const wantsSettings = params.get('panel') === 'settings'

  // `?panel=settings` (the list's "Edit settings" — its rows are units) opens
  // the unit's settings. Without anything to do in them, the flag is dropped.
  useEffect(() => {
    if (!wantsSettings || !unit) return
    if (entry.header) setSettingsFor(unit.id)
    else patchParams({ panel: null })
  }, [wantsSettings, unit, entry.header]) // patchParams is rebuilt every render

  const closeSettings = () => {
    setSettingsFor(null)
    if (wantsSettings) patchParams({ panel: null })
  }

  const crumbs = useMemo(() => {
    if (!selected) return []
    const base = crumbsFor(nodes, holder?.id ?? selected.id)
    return holder && selected.id !== holder.id ? [...base, selected] : base
  }, [nodes, holder, selected])

  /**
   * Select a node. Choosing a slot brings forward what it is chosen for — its
   * contents on Inventory, what it is (and blocking it) on Equipment; a
   * container keeps whichever tab is open.
   */
  const selectNode = (id: string, path?: string, slot = false) => {
    if (path) setClicked({ id, path })
    const patch: Record<string, string | null> = { node: id === unitId ? null : id }
    const slotTab: PanelTab = layoutOnly ? 'selected' : 'contents'
    if (slot && tabs.includes(slotTab)) patch.panel = slotTab
    patchParams(patch)
  }

  const onLocated = (nodeId: string, path: string) => {
    // A hit can live in another unit — follow it there, on the same surface.
    const owner = ownerUnitOf(path, roots.data ?? [])
    if (owner && owner.id !== unitId) {
      navigate(unitPageHref(surface, owner.id, { node: nodeId, panel: 'contents' }))
      return
    }
    selectNode(nodeId, path, true)
  }

  const unitCondition = unit ? resolveKeptAt([unit]).condition : null
  const summaryRow = (summary.data ?? []).find((r) => r.nodeId === unitId) ?? null
  const zoneName = unit?.zoneId ? ((zones.data ?? []).find((z) => z.id === unit.zoneId)?.name ?? null) : null

  const freeSlot = selected != null && selectedIsSlot && !selected.blocked && !occupiedIds.has(selected.id)
  const putStock = () => {
    if (freeSlot && selected) {
      setPlacingFor(selected.id)
      patchParams({ panel: 'contents' })
    } else {
      setReceiving(true)
    }
  }

  if (tree.isLoading) {
    return (
      <Shell>
        <p className='py-10 text-center text-sm text-muted-foreground'>Loading…</p>
      </Shell>
    )
  }

  if (tree.isError || !unit) {
    return (
      <Shell>
        <UnitBreadcrumb listHref={listHref} crumbs={[]} onJump={() => undefined} />
        <Card className='mt-4'>
          <CardContent className='flex flex-col items-center gap-3 py-10 text-center text-sm text-muted-foreground'>
            {tree.isError ? (
              !inventoryParts ? (
                // Every read on this page is an inventory route.
                NO_INVENTORY_ACCESS
              ) : (
                <>
                  Failed to load storage unit.
                  <Button variant='outline' size='sm' onClick={() => tree.refetch()}>
                    Retry
                  </Button>
                </>
              )
            ) : (
              <>
                That storage unit no longer exists.
                <Link to={listHref} className='text-primary hover:underline'>
                  Back to storage
                </Link>
              </>
            )}
          </CardContent>
        </Card>
      </Shell>
    )
  }

  // The unit, or the selection Edit settings was pressed on. A selection that
  // has since moved on (a `?node=` change) closes nothing; it just has no form.
  const settingsTarget =
    settingsFor === unit.id
      ? { node: unit, chain: [unit] }
      : selected && settingsFor === selected.id
        ? { node: selected, chain: crumbs }
        : null

  const gridOfSlots = gridNode != null && isOccupiable(gridNode.gridChildKind ?? 'POSITION')
  const gridFree = gridNode ? (gridNode as StorageTreeNode).capacity?.remaining : undefined

  return (
    <Shell>
      <UnitBreadcrumb
        listHref={listHref}
        crumbs={crumbs}
        onJump={(n) => selectNode(n.id, n.path, isOccupiable(n.kind))}
      />

      <div className='mb-4 mt-2'>
        <UnitHeader
          unit={unit}
          condition={unitCondition}
          summary={unitSummaryParts(capacity.data ?? null, zoneName, layoutOnly)}
          status={summaryRow ? unitStatus(summaryRow) : null}
          onLocated={showContents ? onLocated : undefined}
          onSettings={entry.header ? () => setSettingsFor(unit.id) : undefined}
          settingsOpen={settingsFor === unit.id}
          onPutStock={showContents && canManage ? putStock : undefined}
        />
      </div>

      {/* Above the map on purpose: free slots in a freezer that is switched
          off is the misreading this banner exists to prevent. */}
      {summaryRow?.unavailable && (
        <div className='mb-4'>
          <UnitAvailabilityBanner unavailable={summaryRow.unavailable} />
        </div>
      )}
      {unit.equipmentId && (
        <div className='mb-4'>
          {/* Keyed so an open dialog does not carry over to the next unit. */}
          <UnitMaintenancePanel key={unit.equipmentId} equipmentId={unit.equipmentId} />
        </div>
      )}

      {/* Three page columns on xl — tree, map, a 400px panel — with the map
          taking the rest. Below xl the panel drops under the map. The side
          columns stick, so scrolling a tall box keeps them in view. */}
      <div className='grid items-start gap-4 lg:grid-cols-[minmax(0,16rem)_minmax(0,1fr)] xl:grid-cols-[minmax(0,16rem)_minmax(0,1fr)_400px]'>
        <div className='space-y-4 lg:sticky lg:top-4'>
        {/* The outside of the address first, then the inside (z8nrz7fw25). */}
        <UnitWhereabouts equipmentId={unit.equipmentId} unitCode={unit.code} />
        <Card>
          <CardContent className='space-y-3 p-3'>
            <h2 className='px-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground'>
              Inside {unit.code}
            </h2>
            <StorageBrowser
              nodes={nodes}
              rootId={unit.id}
              selectedId={holder?.id ?? null}
              onSelect={(id) => selectNode(id)}
              containerCount={tree.containerCount}
              onExpand={ensureLoaded}
              layoutOnly={layoutOnly}
            />
            {canConfigure && !isEmpty && (
              <Button size='sm' variant='outline' className='w-full' onClick={() => setWizardOpen(true)}>
                ＋ Add rack or box
              </Button>
            )}
          </CardContent>
        </Card>
        </div>

        <Card className='min-w-0'>
          <CardContent className='space-y-3 p-4'>
            {gridNode ? (
              <>
                <p className='text-sm'>
                  <span className='font-semibold'>{gridNode.name ?? gridNode.code}</span>
                  <span className='text-muted-foreground'>
                    {' · '}
                    {gridNode.gridRows} rows × {gridNode.gridColumns} columns
                    {!layoutOnly && gridFree != null && ` · ${gridFree} free`}
                  </span>
                </p>
                {gridPending ? (
                  <p className='py-10 text-center text-sm text-muted-foreground'>Loading…</p>
                ) : (
                  <SlotMap
                    parent={gridNode}
                    positions={gridCells}
                    occupiedIds={occupiedIds}
                    fills={layoutOnly ? undefined : fills}
                    lotsByNodeId={lotsByNodeId}
                    onSelect={(n) => selectNode(n.id, n.path, isOccupiable(n.kind))}
                    selectedId={selected?.id ?? null}
                    layoutOnly={layoutOnly}
                    maxCellPx={MAX_CELL_PX}
                  />
                )}
                {/* Below the map, not beside the title: a top-row slot's hover
                    card opens upwards and would sit over it. */}
                {gridOfSlots && <SlotMapLegend layoutOnly={layoutOnly} />}
                <p className='text-xs text-muted-foreground'>
                  {!gridOfSlots
                    ? layoutOnly
                      ? 'Click one to open it.'
                      : 'Click one to open it. The bar shows how full it is.'
                    : layoutOnly
                      ? (equipmentSlotHint({ canName: canConfigure, canBlock: canManage }) ?? '')
                      : "Click a slot to see what's in it."}{' '}
                  Use <kbd className={KBD}>←</kbd> <kbd className={KBD}>→</kbd> <kbd className={KBD}>↑</kbd>{' '}
                  <kbd className={KBD}>↓</kbd> to move and <kbd className={KBD}>Enter</kbd> to open.
                  {gridOfSlots && ' Blocked slots are striped as well as coloured.'}
                </p>
              </>
            ) : holder && !isOccupiable(holder.kind) && hasChildren(nodes, holder.id) ? (
              <p className='py-10 text-center text-sm text-muted-foreground'>
                {holder.code} has no grid of its own. What it holds is listed on the left.
              </p>
            ) : holder && !isOccupiable(holder.kind) ? (
              <div className='space-y-4'>
                <p className='text-sm text-muted-foreground'>
                  Nothing is described inside {holder.code} yet.
                  {!canConfigure && ' Someone who can configure storage needs to set it up.'}
                </p>
                {canConfigure && isEmpty && holder.id === unit.id && (
                  <Button onClick={() => setWizardOpen(true)}>Set up this unit</Button>
                )}
                {canConfigure && <DeclareGridForm key={holder.id} node={holder} />}
              </div>
            ) : (
              <p className='py-10 text-center text-sm text-muted-foreground'>
                A slot holds stock rather than more shelves.
              </p>
            )}
          </CardContent>
        </Card>

        {selected && tab && (
          <Card className='min-w-0 lg:col-span-2 xl:sticky xl:top-4 xl:col-span-1'>
            <UnitSidePanel
              node={selected}
              nodes={nodes}
              chain={crumbs}
              tab={tab}
              tabs={tabs}
              onTabChange={(t: PanelTab) => patchParams({ panel: t })}
              occupiedIds={occupiedIds}
              canManage={canManage}
              placing={placingFor === selected.id}
              onPlacingChange={(on) => setPlacingFor(on ? selected.id : null)}
              canBlock={canManage}
              onEditSettings={entry.selected ? () => setSettingsFor(selected.id) : undefined}
              zones={zones.data ?? []}
              capacity={selected.id === unit.id ? (capacity.data ?? null) : ((selected as StorageTreeNode).capacity ?? null)}
              onJump={(n) => selectNode(n.id, n.path, isOccupiable(n.kind))}
              onAddInside={canConfigure && !isEmpty ? () => setWizardOpen(true) : undefined}
            />
          </Card>
        )}
      </div>

      {/* Keyed on the node: a draft for one node means nothing on the next. */}
      {settingsTarget && (
        <NodeSettingsForm
          key={settingsTarget.node.id}
          node={settingsTarget.node}
          chain={settingsTarget.chain}
          nodes={nodes}
          isUnit={settingsTarget.node.id === unit.id}
          canConfigure={canConfigure}
          onReplace={settingsTarget.node.id === unit.id && canReplace ? () => setReplacing(true) : undefined}
          onDelete={settingsTarget.node.id === unit.id && canConfigure ? () => setDeleting(true) : undefined}
          onDone={closeSettings}
        />
      )}

      {/* Modal, not inline: describing a layout is a task with a start and an
          end. On a unit with levels already, it only fills in gaps. */}
      <StorageWizardDialog root={unit} open={wizardOpen} extending={!isEmpty} onClose={() => setWizardOpen(false)} />

      {canReplace && unit.equipmentId && (
        <ReplaceUnitDialog
          open={replacing}
          onClose={() => setReplacing(false)}
          sourceRootId={unit.id}
          sourceCode={unit.code}
          onPlanned={(transfer) => navigate(`/inventory/storage-transfers/${transfer.id}`)}
        />
      )}
      {/* Back to the list on success: staying would leave the page pointed at
          a unit that no longer exists. */}
      <DeleteStorageUnitDialog
        unitId={deleting ? unit.id : null}
        unitLabel={unit.name ?? unit.code}
        onClose={() => setDeleting(false)}
        onDeleted={() => navigate(storageListHref(surface))}
      />

      {/* Receiving into this unit, when no free slot is selected: the
          material list is narrowed to what this unit can keep. */}
      <ReceiveStockDialogs
        open={receiving}
        onClose={() => setReceiving(false)}
        destinationCondition={unitCondition}
      />
    </Shell>
  )
}

function Shell({ children }: { children: ReactNode }) {
  return (
    <div className='min-h-full bg-muted/20'>
      <div className='w-full px-6 py-6'>{children}</div>
    </div>
  )
}
